import React from 'react';
import { Select } from 'antd';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import {
  Banner,
  Card,
  Checkbox,
  ComboRadio,
  Gap,
  InputNumber,
  Paragraph,
  Text,
} from '@common/components';
import { FteAttainmentPeriod } from '@common/graphql/schema';
import { WorkforceGroupFormProps } from './workforce-group-form';
import type { UpdateWorkforceGroupInputDataType } from '@scheduling/components';

type PropsType = Partial<
  Pick<
    UpdateWorkforceGroupInputDataType,
    | 'fteAttainmentEnabled'
    | 'fteAttainmentMethod'
    | 'fteAttainmentPeriod'
    | 'fteFixedHours'
  >
> & {
  onUpdateInputData: WorkforceGroupFormProps['onUpdateInputData'];
};

const EVALUATION_PERIOD_OPTIONS: {
  value: FteAttainmentPeriod;
  label: string;
}[] = [
  { value: 'PAY_PERIOD', label: 'Pay period' },
  { value: 'SCHEDULE_WINDOW', label: 'Schedule window' },
  { value: 'WEEKLY', label: 'Weekly' },
];

export const WorkforceGroupFormIncentiveEligibilityTab = ({
  onUpdateInputData,
  fteAttainmentEnabled,
  fteAttainmentPeriod,
  fteAttainmentMethod,
  fteFixedHours,
}: PropsType) => {
  const onToggle = (e: CheckboxChangeEvent) => {
    onUpdateInputData('fteAttainmentEnabled', e.target.checked);
  };

  const fixedHoursSelected = fteAttainmentMethod === 'FIXED_HOURS';
  const requiredHoursDisabled = !fixedHoursSelected;

  return (
    <Card>
      <Gap vertical size="lg">
        <Checkbox checked={!!fteAttainmentEnabled} onChange={onToggle}>
          Set incentive eligibility rules
        </Checkbox>
        <Paragraph>
          When enabled, staff must meet the criteria below to qualify for
          incentives. The criteria can be configured in advance while disabled.
        </Paragraph>

        <Gap vertical fullWidth size="xs">
          <Text weight="bold">Evaluation period</Text>
          <Paragraph color="midnightDarkest">
            The period used to determine if staff qualify for incentives.
          </Paragraph>
          <Select
            value={fteAttainmentPeriod || undefined}
            placeholder="Select period"
            options={EVALUATION_PERIOD_OPTIONS}
            onChange={(value) =>
              onUpdateInputData('fteAttainmentPeriod', value)
            }
            size="large"
            style={{ width: 200 }}
          />
        </Gap>

        <Gap vertical fullWidth size="xs">
          <Text weight="bold">Eligibility method</Text>
          <Paragraph color="midnightDarkest">
            How a staff member&apos;s eligibility is calculated.
          </Paragraph>
          <ComboRadio
            value={fteAttainmentMethod || undefined}
            onChange={(e) =>
              onUpdateInputData('fteAttainmentMethod', e.target.value)
            }
            options={[
              { value: 'WORKER_PROFILE_FTE', label: 'Based on staff FTE %' },
              {
                value: 'FIXED_HOURS',
                label: 'Fixed hours per period',
                content: (
                  <Gap vertical size="xs">
                    <Text weight="bold">
                      Required hours (if using fixed hours)
                    </Text>
                    <Paragraph color="midnightDarkest">
                      The number of hours required to qualify within the
                      selected period.
                    </Paragraph>
                    <InputNumber
                      min={0}
                      step={0.25}
                      value={fteFixedHours}
                      disabled={requiredHoursDisabled}
                      onChange={(v) =>
                        onUpdateInputData('fteFixedHours', Number(v ?? 0))
                      }
                      size="large"
                      style={{ width: 115 }}
                    />
                  </Gap>
                ),
              },
            ]}
          />
        </Gap>

        <Banner type="info" showIcon>
          Eligibility evaluation runs once FTE Attainment is enabled for your
          organization.
        </Banner>
      </Gap>
    </Card>
  );
};
